import React from 'react'

import { Box, Button, Container, Grid, Link, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import Image from 'artkive/components/Image'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import HowItWorks from 'artkive/components/PageSections/HowItWorks'
import Social from 'artkive/components/Social'
import TeamCarousel from 'artkive/components/TeamCarousel'
import { TrackButton, withClickTracker } from 'artkive/components/Tracker'
import usePageScrollTracker from 'artkive/components/Tracker/usePageScrollTracker'
import { GET_BOX_CLICK, NAVIGATION } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import routes from 'artkive/utils/routes'
import theme from 'artkive/utils/theme'

import Badge from 'images/ecom/process/Process-Badges-Careful.svg'
import Badge2 from 'images/ecom/process/Process-Badges-Quality.svg'
import Badge3 from 'images/ecom/process/Process-Badges-Team.svg'
import Badge4 from 'images/ecom/process/Process-Badges-Updates.svg'

import GetStartedFooter from '../Home/GetStartedFooter'

import HeadSection from './HeadSection'
import { ourProcessHowItWorksSteps } from './OurProcess.constants'

const TrackSwipe = withClickTracker(Button)

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    overflowX: 'hidden',
    backgroundColor: theme.colors.white.dark,
  },
})

const useAboutItemStyles = makeStyles({
  image: {
    borderRadius: 8,
  },
  item: {
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  stepIndex: {
    color: '#7A68AE',
    fontWeight: 700,
  },
})

const AboutHowItWorksItem = ({ index, title, text, image, order }) => {
  const classes = useAboutItemStyles()
  const isMobile = useMobileBreakpoint()

  return (
    <Box my={4}>
      <Grid container spacing={isMobile ? 0 : 4} direction={isMobile ? 'column-reverse' : order}>
        <Grid item xs={12} md={6} className={classes.item}>
          <Box px={isMobile ? 2 : 8}>
            <Typography variant='body1' className={classes.stepIndex}>
              STEP {index}
            </Typography>
            <Box mb={2} mt={2}>
              <Typography variant='h3' align={isMobile ? 'center' : 'left'}>
                {title}
              </Typography>
            </Box>
            <Typography variant='body1' align={isMobile ? 'center' : 'left'}>
              {text}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Image src={image} className={classes.image} />
        </Grid>
      </Grid>
    </Box>
  )
}

const OurProcess = () => {
  const classes = useStyles()
  usePageScrollTracker()

  const isMobile = useMobileBreakpoint()
  const { boxCheckoutUrl } = useSale()

  return (
    <div className={classes.root}>
      <HeadSection />
      {isMobile ? (
        <HowItWorks
          steps={ourProcessHowItWorksSteps}
          title='Here’s How It Works'
          subtitle={
            <Box mt={3}>
              <Typography align='center'>How the art you love becomes a keepsake you'll treasure</Typography>
            </Box>
          }
          variant='image'
          bgcolor='white'
        >
          <Typography align='center'>How the art you love becomes a keepsake you'll treasure</Typography>
          <Box justifyContent='center' display='flex' mt={isMobile ? 6 : 8}>
            <TrackButton
              color='primary'
              variant='contained'
              href={boxCheckoutUrl}
              trackerEvent={GET_BOX_CLICK}
              trackerOptions={{ section: 'out_process' }}
              size='large'
            >
              Get My Box
            </TrackButton>
          </Box>
        </HowItWorks>
      ) : (
        <ContainedSection
          title={
            <>
              <Typography gutterBottom variant='h2' align='center'>
                Here’s How It Works
              </Typography>
              <Typography align='center'>How the art you love becomes a keepsake you'll treasure</Typography>
            </>
          }
          bgcolor='white'
        >
          {ourProcessHowItWorksSteps.map((step, index) => (
            <AboutHowItWorksItem
              key={index}
              order={index % 2 ? 'row-reverse' : 'row'}
              title={step.title}
              text={step.body}
              image={step.image}
              index={index + 1}
            />
          ))}

          <Box justifyContent='center' display='flex' mt={isMobile ? 6 : 8}>
            <TrackButton
              color='primary'
              variant='contained'
              href={boxCheckoutUrl}
              trackerEvent={GET_BOX_CLICK}
              trackerOptions={{ section: 'home_how_it_works' }}
              size='large'
            >
              Get My Box
            </TrackButton>
          </Box>
        </ContainedSection>
      )}
      <Box py={8}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box textAlign='center'>
                <Box mb={2}>
                  <Image src={Badge} style={{ maxWidth: '100px' }} />
                </Box>
                <Box mb={2}>
                  <Typography gutterBottom style={{ fontWeight: 'bold', color: theme.colors.primary.dark }}>
                    Handled With Care
                  </Typography>
                </Box>
                <Typography>We handle each piece with love. No feathers will be lost in the process.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign='center'>
                <Box mb={2}>
                  <Image src={Badge2} style={{ maxWidth: '100px' }} />
                </Box>
                <Box mb={2}>
                  <Typography gutterBottom style={{ fontWeight: 'bold', color: theme.colors.primary.dark }}>
                    Quality Guarantee
                  </Typography>
                </Box>
                <Typography>
                  Our quality standards are high. If something&apos;s wrong, we&apos;ll make it right.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign='center'>
                <Box mb={2}>
                  <Image src={Badge3} style={{ maxWidth: '100px' }} />
                </Box>
                <Box mb={2}>
                  <Typography gutterBottom style={{ fontWeight: 'bold', color: theme.colors.primary.dark }}>
                    Dedicated Support
                  </Typography>
                </Box>
                <Typography>From start to finish, we&apos;re committed to taking care of you like family. </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box textAlign='center'>
                <Box mb={2}>
                  <Image src={Badge4} style={{ maxWidth: '100px' }} />
                </Box>
                <Box mb={2}>
                  <Typography gutterBottom style={{ fontWeight: 'bold', color: theme.colors.primary.dark }}>
                    Real-Time Updates
                  </Typography>
                </Box>
                <Typography>
                  Stay informed with email updates and access to your{' '}
                  <strong>
                    <Link href={routes.accountOrders} target='_blank'>
                      My Orders page
                    </Link>
                  </strong>
                  .
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <ContainedSection
        bgcolor='white'
        disableGutters={isMobile}
        title={
          <>
            <Box mb={4} px={2}>
              <Typography align='center' variant='h2'>
                A creative dream team
              </Typography>
            </Box>
            <Box mb={4} px={isMobile ? 2 : 10} mx={isMobile ? 0 : 10}>
              <Typography align='center'>
                We&apos;re a passionate team of creatives who&apos;ve come together with the same mission: to lift up
                the creators of tomorrow and leave no piece of art behind.
              </Typography>
            </Box>
          </>
        }
      >
        <TeamCarousel />
        <Box align='center' mt={6}>
          <TrackSwipe
            variant='outlined'
            size='large'
            color='primary'
            underline='none'
            href={routes.aboutUs}
            trackerEvent={NAVIGATION.ABOUT_US_CLICK}
            trackerOptions={{
              section: 'about_us_banner',
            }}
          >
            About Us
          </TrackSwipe>
        </Box>
      </ContainedSection>
      <Social />
      <GetStartedFooter />
    </div>
  )
}

export default withApp(OurProcess)
